import React, { useState, useEffect } from 'react';
import {
  Button,
  Modal,
  Upload,
  message,
  Card,
  Row,
  Col,
  DatePicker,
  Select,
  Alert,
  Spin,
  Checkbox,
  Space,
  Divider,
  Empty,
  Table,
  Radio,
  Tooltip as AntTooltip,
  Grid
} from 'antd';
import { UploadOutlined, SearchOutlined, InfoCircleOutlined, BarChartOutlined, TableOutlined, DownloadOutlined, ExpandOutlined, FileImageOutlined } from '@ant-design/icons';
import { SidebarLayout } from '../components/SidebarLayout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer 
} from 'recharts';
import locale from 'antd/es/date-picker/locale/pt_BR';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

dayjs.locale('pt-br');
const { RangePicker } = DatePicker;
const { Option } = Select;

const api = `${process.env.REACT_APP_API_URL}/iot_messages`;

const { useBreakpoint } = Grid;

const Reports = () => {
  const { token, selectedClient } = useAuth();
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [dateRange, setDateRange] = useState([]);
  const [selectedHorse, setSelectedHorse] = useState(null);
  const [selectedTrayler, setSelectedTrayler] = useState(null);
  const [horses, setHorses] = useState([]);
  const [traylers, setTraylers] = useState([]);
  const [messageApi, contextHolder] = message.useMessage();
  const [trailerLinkedToHorse, setTrailerLinkedToHorse] = useState(true);
  const [showLinkWarning, setShowLinkWarning] = useState(false);  const [searchResults, setSearchResults] = useState([]);  const [uniquePositions, setUniquePositions] = useState([]);
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [visibleSeries, setVisibleSeries] = useState(['nrtemp']);
  const [chartData, setChartData] = useState([]);
  const [viewMode, setViewMode] = useState('chart');
  const [fullScreenMode, setFullScreenMode] = useState(false);
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  useEffect(() => {
    if (selectedClient) {
      fetchHorses();
      fetchTraylers();
    }
  }, [selectedClient]);

  useEffect(() => {
    if (selectedHorse && selectedTrayler) {
      const horse = horses.find(h => h.id === selectedHorse);
      if (horse) {
        setTrailerLinkedToHorse(horse.idtrayler === selectedTrayler);
        setShowLinkWarning(horse.idtrayler !== selectedTrayler);
      }
    } else {
      setShowLinkWarning(false);
    }
  }, [selectedHorse, selectedTrayler, horses]);

  useEffect(() => {
    if (searchResults.length > 0) {
      processChartData();
    }
  }, [searchResults, selectedPositions, visibleSeries]);

  const fetchHorses = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/truck_horses?selectedClient=${selectedClient.client.id}`);
      setHorses(res.data);
    } catch (error) {
      messageApi.error('Erro ao buscar cavalos');
    }
  };

  const fetchTraylers = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/truck_traylers?selectedClient=${selectedClient.client.id}`);
      setTraylers(res.data);
    } catch (error) {
      messageApi.error('Erro ao buscar carretas');
    }
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      messageApi.error('Por favor, selecione um arquivo CSV para importar');
      return;
    }

    const formData = new FormData();
    formData.append('file', fileList[0].originFileObj);

    setLoading(true);
    try {
      const response = await axios.post(`${api}/upload_csv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        timeout: 900000
      });

      const saved = response.data?.mensagens_salvas ?? 0;
      const events = response.data?.eventos_salvos ?? 0;
      const ignored = (response.data?.linhas_ignoradas ?? 0) + (response.data?.duplicados_ignorados ?? 0);
      const unknown = response.data?.dispositivos_nao_encontrados ?? 0;
      const details = [`${saved} mensagens`, `${events} eventos`];
      if (ignored) details.push(`${ignored} linhas ignoradas`);
      if (unknown) details.push(`${unknown} sensores não cadastrados`);
      messageApi.success(`Arquivo importado: ${details.join(', ')}`);
      setUploadModalVisible(false);
      setFileList([]);
    } catch (error) {
      console.error('Erro ao enviar arquivo:', error);
      if (error.response && error.response.data && error.response.data.error) {
        messageApi.error(error.response.data.error);
      } else if (error.response && error.response.status === 409) {
        messageApi.error('Este arquivo já foi importado anteriormente');
      } else {
        messageApi.error('Erro ao importar arquivo');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!dateRange || dateRange.length !== 2) {
      messageApi.error('Por favor, selecione uma data inicial e final');
      return;
    }

    if (!selectedHorse && !selectedTrayler) {
      messageApi.error('Por favor, selecione um cavalo ou carreta');
      return;
    }

    setSearchLoading(true);
    try {
      const startDate = dateRange[0].format('YYYY-MM-DD');
      const endDate = dateRange[1].format('YYYY-MM-DD');
      
      let queryParams = `startDate=${startDate}&endDate=${endDate}`;
      if (selectedHorse) queryParams += `&horseId=${selectedHorse}`;
      if (selectedTrayler) queryParams += `&traylerId=${selectedTrayler}`;

      const response = await axios.get(`${api}/search?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setSearchResults(response.data.messages);
      
      const positions = [...new Set(response.data.messages.map(msg => msg.nrposition))];
      setUniquePositions(positions);
      setSelectedPositions(positions);
      messageApi.success(`${response.data.count} registros encontrados`);
    } catch (error) {
      console.error('Erro na pesquisa:', error);
      if (error.response && error.response.data && error.response.data.error) {
        messageApi.error(error.response.data.error);
      } else {
        messageApi.error('Erro ao realizar pesquisa');
      }
    } finally {
      setSearchLoading(false);
    }
  };

  const processChartData = () => {
    // Agrupa por dtmessage (data/hora como string)
    const groupedByDate = {};
    const idByDate = {};
    searchResults.forEach(item => {
      if (!selectedPositions.includes(item.nrposition)) return;
      const dateKey = item.dtmessage;
      if (!groupedByDate[dateKey]) {
        groupedByDate[dateKey] = {
          date: dateKey,
          dateObj: new Date(item.dtmessage)
        };
        idByDate[dateKey] = item.id;
      }
      // Garante que o menor id seja usado para ordenação
      if (item.id < idByDate[dateKey]) idByDate[dateKey] = item.id;
      const posKey = `pos_${item.nrposition}`;
      groupedByDate[dateKey][`${posKey}_nrtemp`] = item.nrtemp;
      groupedByDate[dateKey][`${posKey}_nrbattery`] = item.nrbattery;
      groupedByDate[dateKey][`${posKey}_nrrssi`] = item.nrrssi;
      groupedByDate[dateKey][`${posKey}_nrsnr`] = item.nrsnr;
      groupedByDate[dateKey][`${posKey}_id`] = item.id;
    });
    // Ordena por menor id de cada grupo
    const chartDataArray = Object.entries(groupedByDate)
      .map(([date, obj]) => ({ ...obj, _sortId: idByDate[date] }))
      .sort((a, b) => a._sortId - b._sortId);
    setChartData(chartDataArray);
  };

  const uploadProps = {
    beforeUpload: (file) => {
      const isCsv = file.type === 'text/csv' || file.name.endsWith('.csv');
      if (!isCsv) {
        messageApi.error('Apenas arquivos CSV são permitidos');
        return Upload.LIST_IGNORE;
      }
      return false;
    },
    fileList,
    onChange: ({ fileList }) => {
      if (fileList.length > 1) {
        fileList = [fileList[fileList.length - 1]];
      }
      setFileList(fileList);
    },
    maxCount: 1
  };

  const handlePositionChange = (position) => {
    if (selectedPositions.includes(position)) {
      setSelectedPositions(selectedPositions.filter(p => p !== position));
    } else {
      setSelectedPositions([...selectedPositions, position]);
    }
  };

  const toggleSeries = (series) => {
    if (visibleSeries.includes(series)) {
      setVisibleSeries(visibleSeries.filter(s => s !== series));
    } else {
      setVisibleSeries([...visibleSeries, series]);
    }
  };
  const exportToCSV = () => {
    if (chartData.length === 0) {
      messageApi.warning('Não há dados para exportar');
      return;
    }

    let headers = ['Data'];
    [...selectedPositions].sort((a, b) => a - b).forEach(position => {
      visibleSeries.forEach(series => {
        let unidade = '';
        if (series === 'nrtemp') unidade = ' (°C)';
        else if (series === 'nrbattery') unidade = ' (mV)';
        else if (series === 'nrrssi') unidade = ' (dBm)';
        else if (series === 'nrsnr') unidade = ' (dB)';
        headers.push(`Posição ${position} - ${seriesLabels[series]}${unidade}`);
      });
    });

    const rows = chartData.map(data => {
      const date = new Date(data.date);
      const formattedDate = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
      const row = [formattedDate];
      [...selectedPositions].sort((a, b) => a - b).forEach(position => {
        visibleSeries.forEach(series => {
          const key = `pos_${position}_${series}`;
          const value = data[key];
          if (value === undefined) {
            row.push('-');
          } else if (series === 'nrtemp') {
            row.push(value.toFixed(1));
          } else {
            row.push(value);
          }
        });
      });
      return row;
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    const now = new Date();
    const filename = `dados_${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}_${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}.csv`;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    messageApi.success(`Arquivo ${filename} baixado com sucesso`);
  };
  
  const positionColors = {
    1: '#1890ff',
    2: '#52c41a',
    3: '#faad14',
    4: '#f5222d',
    5: '#722ed1',
    6: '#13c2c2',
    7: '#fa8c16',
    8: '#eb2f96',
    9: '#a0d911',
    10: '#2f54eb',
    11: '#fa541c',
    12: '#9254de',
    13: '#006d75',
    14: '#d4380d',
    15: '#5cdbd3',
    16: '#f759ab',
    17: '#614700',
    18: '#08979c',
  };
  const exportToImage = () => {
    if (chartData.length === 0) {
      messageApi.warning('Não há dados para exportar');
      return;
    }

    const containerId = fullScreenMode ? 'fullscreen-chart-container' : 'chart-container';
    const chartContainer = document.getElementById(containerId);
    
    if (!chartContainer) {
      messageApi.error('Elemento do gráfico não encontrado');
      return;
    }

    import('html2canvas').then(html2canvas => {
      messageApi.loading({
        content: 'Gerando imagem...',
        key: 'exportImage'
      });

      html2canvas.default(chartContainer, {
        scale: 2,
        logging: false,
        useCORS: true,
        backgroundColor: '#ffffff'
      }).then(canvas => {
        const imgData = canvas.toDataURL('image/png');
        
        const link = document.createElement('a');
        const now = new Date();
        const filename = `grafico_${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}_${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}.png`;
        
        link.download = filename;
        link.href = imgData;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        messageApi.success({
          content: `Imagem ${filename} baixada com sucesso`,
          key: 'exportImage'
        });
      }).catch(error => {
        console.error('Erro ao gerar imagem:', error);
        messageApi.error({
          content: 'Erro ao gerar imagem',
          key: 'exportImage'
        });
      });
    });
  };
  
  const seriesLabels = {
    nrtemp: 'Temperatura',
    nrbattery: 'Bateria',
    nrrssi: 'RSSI',
    nrsnr: 'SNR'
  };

  const getHorsePlate = (id) => {
    const horse = horses.find(h => h.id === id);
    return horse ? horse.dsplate : '';
  };

  const getTrailerPlate = (id) => {
    const trailer = traylers.find(t => t.id === id);
    return trailer ? trailer.dsplate : '';
  };

  const customColumnStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  };

  return (
    <SidebarLayout>
      {contextHolder}
      <Card bordered={false}>
        <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
          <Col>
            <h1 style={{ fontSize: 30, margin: 0 }}>
              Relatórios
            </h1>
          </Col>
          <Col>
            <Button
              icon={<UploadOutlined />}
              onClick={() => setUploadModalVisible(true)}
            >
              Importar CSV
            </Button>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={24}>
            <Card>
              <Row gutter={[16, 16]}>
                <Col xs={24} md={10}>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', marginBottom: 8 }}>Período:</label>
                    <RangePicker
                      style={{ width: '100%' }}
                      locale={locale}
                      format="DD/MM/YYYY"
                      value={dateRange}
                      onChange={(dates) => setDateRange(dates)}
                    />
                  </div>
                </Col>
                <Col xs={24} md={10}>
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <div style={{ marginBottom: 16 }}>
                        <label style={{ display: 'block', marginBottom: 8 }}>Cavalo:</label>
                        <Select
                          style={{ width: '100%' }}
                          placeholder="Selecione um cavalo"
                          value={selectedHorse}
                          onChange={setSelectedHorse}
                          allowClear
                          showSearch
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option.children.toLowerCase().includes(input.toLowerCase())
                          }
                        >
                          {horses.map(horse => (
                            <Option key={horse.id} value={horse.id}>{horse.dsplate}</Option>
                          ))}
                        </Select>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div style={{ marginBottom: 16 }}>
                        <label style={{ display: 'block', marginBottom: 8 }}>Carreta:</label>
                        <Select
                          style={{ width: '100%' }}
                          placeholder="Selecione uma carreta"
                          value={selectedTrayler}
                          onChange={setSelectedTrayler}
                          allowClear
                          showSearch
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option.children.toLowerCase().includes(input.toLowerCase())
                          }
                        >
                          {traylers.map(trayler => (
                            <Option key={trayler.id} value={trayler.id}>{trayler.dsplate}</Option>
                          ))}
                        </Select>
                      </div>
                    </Col>
                  </Row>
                </Col>
                <Col xs={24} md={4} style={{ display: 'flex', alignItems: 'flex-end', marginBottom: 16 }}>
                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={handleSearch}
                    loading={searchLoading}
                    style={{ width: '100%' }}
                  >
                    Pesquisar
                  </Button>
                </Col>
              </Row>

              <Row>
                <Col span={24}>
                  {showLinkWarning && (
                    <Alert
                      description={`A carreta ${getTrailerPlate(selectedTrayler)} não está atualmente vinculada ao cavalo ${getHorsePlate(selectedHorse)}, mas você ainda pode fazer a consulta.`}
                      type="warning"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                  )}
                </Col>
              </Row>
            </Card>
          </Col>
          
          <Col xs={24} lg={8}>
            <Card title="Filtros">
              <div style={{ marginBottom: 16 }}>
                <h4>Variáveis</h4>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Checkbox
                    checked={visibleSeries.includes('nrtemp')}
                    onChange={() => toggleSeries('nrtemp')}
                  >
                    Temperatura
                  </Checkbox>
                  <Checkbox
                    checked={visibleSeries.includes('nrbattery')}
                    onChange={() => toggleSeries('nrbattery')}
                  >
                    Bateria
                  </Checkbox>
                  <Checkbox
                    checked={visibleSeries.includes('nrrssi')}
                    onChange={() => toggleSeries('nrrssi')}
                  >
                    RSSI
                  </Checkbox>
                  <Checkbox
                    checked={visibleSeries.includes('nrsnr')}
                    onChange={() => toggleSeries('nrsnr')}
                  >
                    SNR
                  </Checkbox>
                </Space>
              </div>
              
              <Divider style={{ margin: '16px 0' }} />
              
              <div>
                <h4>Posições</h4>
                {uniquePositions.length > 0 ? (
                  <div>
                    <div style={{ marginBottom: 8 }}>                      <Checkbox
                        checked={selectedPositions.length === uniquePositions.length}
                        indeterminate={selectedPositions.length > 0 && selectedPositions.length < uniquePositions.length}
                        onChange={() => {
                          if (selectedPositions.length === uniquePositions.length) {
                            setSelectedPositions([]);
                          } else {
                            setSelectedPositions([...uniquePositions]);
                          }
                        }}
                      >
                        {selectedPositions.length === uniquePositions.length ? 'Desmarcar todas' : 'Marcar todas'}
                      </Checkbox>
                    </div>
                    <Divider style={{ margin: '8px 0' }} />
                    <Row gutter={[8, 8]}>
                      {[...uniquePositions].sort((a, b) => a - b).map(position => (
                        <Col span={12} key={position}>                          <Checkbox
                            checked={selectedPositions.includes(position)}
                            onChange={() => handlePositionChange(position)}
                          >
                            Posição {position}
                            <span style={{ 
                              display: 'inline-block', 
                              width: '12px', 
                              height: '12px', 
                              borderRadius: '50%', 
                              backgroundColor: positionColors[position] || '#000',
                              marginLeft: '5px'
                            }}></span>
                          </Checkbox>
                        </Col>
                      ))}
                    </Row>
                  </div>
                ) : (
                  <Empty description="Sem posições para exibir" />
                )}
              </div>
            </Card>
          </Col>
            <Col id='data-view' xs={24} lg={16}>
            <Card 
              
              title={
                <Row justify="space-between" align="middle">
                  <Col>Visualização de Dados</Col>
                  <Col>
                    <Space>
                      <Radio.Group
                        value={viewMode}
                        onChange={(e) => setViewMode(e.target.value)}
                        buttonStyle="solid"
                        size="small"
                      >
                        <Radio.Button value="chart">
                          <BarChartOutlined /> Gráfico
                        </Radio.Button>
                        <Radio.Button value="table">
                          <TableOutlined /> Tabela
                        </Radio.Button>                      </Radio.Group>
                        <AntTooltip title="Visualizar em tela cheia">
                        <Button
                          icon={<ExpandOutlined />}
                          size="small"
                          onClick={() => setFullScreenMode(true)}
                          disabled={chartData.length === 0}
                        />
                      </AntTooltip>
                      
                      {viewMode === 'chart' && (
                        <AntTooltip title="Exportar gráfico como PNG">
                          <Button
                            type="primary"
                            icon={<FileImageOutlined />}
                            size="small"
                            onClick={exportToImage}
                            disabled={chartData.length === 0}
                          >
                            PNG
                          </Button>
                        </AntTooltip>
                      )}
                      
                      {viewMode === 'table' && (
                        <AntTooltip title="Exportar dados para CSV">
                          <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            size="small"
                            onClick={exportToCSV}
                            disabled={chartData.length === 0}
                          >
                            CSV
                          </Button>
                        </AntTooltip>
                      )}
                    </Space>
                  </Col>
                </Row>
              }
            >
              {searchLoading ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <Spin size="large" />
                </div>              ) : chartData.length > 0 ? (
                viewMode === 'chart' ? (
                  <div style={{ width: '100%', height: 400 }} id="chart-container">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={chartData}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="date" 
                          tickFormatter={(value) => {
                            const date = new Date(value);
                            return `${date.getDate()}/${date.getMonth() + 1} ${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')}`;
                          }}
                        />
                        {isMobile ? (
                          <YAxis width={24} tick={{ fontSize: 10 }} axisLine={false} />
                        ) : (
                          <YAxis />
                        )}
                        <Tooltip 
                          formatter={(value, name) => {
                            const parts = name.split('_');
                            const position = parts[1];
                            const metric = parts[2];
                            if (metric === 'nrtemp') return [value.toFixed(1) + ' °C', `Pos ${position} - Temperatura`];
                            if (metric === 'nrbattery') return [value + ' mV', `Pos ${position} - Bateria`];
                            if (metric === 'nrrssi') return [value + ' dBm', `Pos ${position} - RSSI`];
                            if (metric === 'nrsnr') return [value + ' dB', `Pos ${position} - SNR`];
                            return [value, name];
                          }}
                          labelFormatter={(label) => {
                            return `Data: ${label}`;
                          }}
                        />
                        
                        {selectedPositions.map(position => (
                          visibleSeries.map(series => (
                            <Line
                              key={`pos_${position}_${series}`}
                              type="monotone"
                              dataKey={`pos_${position}_${series}`}
                              name={`pos_${position}_${series}`}
                              stroke={positionColors[position]}
                              strokeDasharray={series !== 'nrtemp' ? '5 5' : null}
                              activeDot={{ r: 8 }}
                              dot={false}
                            />
                          ))                      ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>                ) : (                  <div style={{ height: 400 }}>                    
                    <Table
                      dataSource={chartData.map((item, index) => ({ ...item, key: index }))}
                      scroll={{ x: 'max-content', y: 300 }}
                      pagination={false}
                      size="small"
                      bordered
                      sticky
                      components={{
                        header: {
                          cell: props => <th {...props} style={{ whiteSpace: 'nowrap', ...props.style }} />
                        },
                        body: {
                          cell: props => <td {...props} style={{ whiteSpace: 'nowrap', ...props.style }} />
                        }
                      }}
                    >                      
                    <Table.Column
                        title={<span style={{ whiteSpace: 'nowrap' }}>Data/Hora</span>} 
                        dataIndex="date" 
                        key="date" 
                        width={180}
                        ellipsis={true}
                        render={(text) => {
                          const date = new Date(text);
                          return <span style={{ whiteSpace: 'nowrap' }}>
                            {`${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`}
                          </span>;
                        }}
                      />
                      {[...selectedPositions].sort((a, b) => a - b).map(position => {
                        const posColumns = visibleSeries.map(series => (
                          <Table.Column 
                            key={`pos_${position}_${series}`}
                            title={seriesLabels[series]}
                            dataIndex={`pos_${position}_${series}`}
                            width={100}
                            ellipsis={true}
                            render={(value) => {
                              if (value === undefined) return '-';
                              if (series === 'nrtemp') return `${value.toFixed(1)} °C`;
                              if (series === 'nrbattery') return `${value} mV`;
                              if (series === 'nrrssi') return `${value} dBm`;
                              if (series === 'nrsnr') return `${value} dB`;
                              
                              return value;
                            }}
                            align="right"
                          />
                        ));

                        return (
                          <Table.ColumnGroup 
                            key={`pos_group_${position}`} 
                            title={
                              <span style={{ whiteSpace: 'nowrap' }}>
                                Posição {position}
                                <span style={{ 
                                  display: 'inline-block', 
                                  width: '10px', 
                                  height: '10px', 
                                  borderRadius: '50%', 
                                  backgroundColor: positionColors[position] || '#000',
                                  marginLeft: '8px',
                                  verticalAlign: 'middle'
                                }}></span>
                              </span>
                            }
                          >
                            {posColumns}
                          </Table.ColumnGroup>
                        );
                      })}
                    </Table>
                  </div>
                )
              ) : (
                <Empty 
                  description="Sem dados para exibir. Faça uma pesquisa para visualizar o gráfico." 
                  style={{ padding: '40px 0' }}
                />
              )}
            </Card>
          </Col>
        </Row>

        <Modal
          title="Importar Arquivo CSV"
          open={uploadModalVisible}
          onCancel={() => {
            setUploadModalVisible(false);
            setFileList([]);
          }}
          onOk={handleUpload}
          confirmLoading={loading}
          okText="Importar"
          cancelText="Cancelar"
        >
          <Upload.Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p className="ant-upload-text">Clique ou arraste o arquivo para esta área para fazer upload</p>
            <p className="ant-upload-hint">Apenas arquivos CSV são suportados</p>          </Upload.Dragger>
        </Modal>

        <Modal          title={`${viewMode === 'chart' ? 'Gráfico' : 'Tabela'} em Tela Cheia`}
          open={fullScreenMode}
          onCancel={() => setFullScreenMode(false)}
          width="90%"
          footer={[
            viewMode === 'chart' && (
              <Button 
                key="export-png" 
                type="primary" 
                icon={<FileImageOutlined />} 
                onClick={exportToImage}
              >
                Exportar como PNG
              </Button>
            ),
            <Button key="close" onClick={() => setFullScreenMode(false)}>
              Fechar
            </Button>
          ]}
          style={{ top: 20 }}
          >
          {chartData.length > 0 && (
            viewMode === 'chart' ? (
              <div
                style={{ width: '100%', height: '400px', minHeight: 400 }}
                id="fullscreen-chart-container"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartData}
                    
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getDate()}/${date.getMonth() + 1} ${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')}`;
                      }}
                    />
                    {!isMobile ? <YAxis /> : <YAxis width={0} tick={false} axisLine={false} />}
                    <Tooltip 
                      formatter={(value, name) => {
                        const parts = name.split('_');
                        const position = parts[1];
                        const metric = parts[2];
                        if (metric === 'nrtemp') return [value.toFixed(1) + ' °C', `Pos ${position} - Temperatura`];
                        if (metric === 'nrbattery') return [value + ' mV', `Pos ${position} - Bateria`];
                        if (metric === 'nrrssi') return [value + ' dBm', `Pos ${position} - RSSI`];
                        if (metric === 'nrsnr') return [value + ' dB', `Pos ${position} - SNR`];
                          return [value, name];
                      }}
                      labelFormatter={(label) => {
                        return `Data: ${label}`;
                      }}
                    />
                    {selectedPositions.map(position => (
                      visibleSeries.map(series => (
                        <Line
                          key={`pos_${position}_${series}`}
                          type="monotone"
                          dataKey={`pos_${position}_${series}`}
                          name={`pos_${position}_${series}`}
                          stroke={positionColors[position]}
                          strokeDasharray={series !== 'nrtemp' ? '5 5' : null}
                          activeDot={{ r: 8 }}
                          dot={false}
                        />
                      ))
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div style={{ height: '100%' }}>
                <Table
                  dataSource={chartData.map((item, index) => ({ ...item, key: index }))}
                  scroll={{ x: 'max-content', y: 310 }}
                  pagination={false}
                  size="small"
                  bordered
                  sticky
                  components={{
                    header: {
                      cell: props => <th {...props} style={{ whiteSpace: 'nowrap', ...props.style }} />
                    },
                    body: {
                      cell: props => <td {...props} style={{ whiteSpace: 'nowrap', ...props.style }} />
                    }
                  }}
                >
                  <Table.Column
                    title="Data/Hora" 
                    dataIndex="date" 
                    key="date" 
                    fixed="left"
                    width={180}
                    ellipsis={true}
                    render={(text) => {
                      const date = new Date(text);
                      return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
                    }}
                  />
                  {[...selectedPositions].sort((a, b) => a - b).map(position => {
                    const posColumns = visibleSeries.map(series => (
                      <Table.Column 
                        key={`pos_${position}_${series}`}
                        title={seriesLabels[series]}
                        dataIndex={`pos_${position}_${series}`}
                        width={100}
                        ellipsis={true}
                        render={(value) => {
                          if (value === undefined) return '-';
                          if (series === 'nrtemp') return `${value.toFixed(1)} °C`;
                          if (series === 'nrbattery') return `${value} mV`;
                          if (series === 'nrrssi') return `${value} dBm`;
                          if (series === 'nrsnr') return `${value} dB`;
                          return value;
                        }}
                        align="right"
                      />
                    ));

                    return (
                      <Table.ColumnGroup 
                        key={`pos_group_${position}`} 
                        title={
                          <span style={{ whiteSpace: 'nowrap' }}>
                            Posição {position}
                            <span style={{ 
                              display: 'inline-block', 
                              width: '10px', 
                              height: '10px', 
                              borderRadius: '50%', 
                              backgroundColor: positionColors[position] || '#000',
                              marginLeft: '8px',
                              verticalAlign: 'middle'
                            }}></span>
                          </span>
                        }
                      >
                        {posColumns}
                      </Table.ColumnGroup>
                    );
                  })}
                </Table>
              </div>
            )
          )}
        </Modal>
      </Card>
    </SidebarLayout>
  );
};

export { Reports };